// Shared promise to deduplicate concurrent refresh calls
let refreshPromise = null;
let logoutCallback = null;
let refreshCallback = null;

const apiService = {
    baseURL: import.meta.env.VITE_ENVIRONMENT === 'development'
        ? '/api'
        : (import.meta.env.VITE_API_URL || '/api'),

    /**
     * Register a callback to be invoked when the user must be logged out.
     * @param {function} callback - receives a reason message string
     */
    onLogout(callback) {
        logoutCallback = callback;
    },

    onSessionRefresh(callback) {
        refreshCallback = callback;
    },

    getHeaders() {
        const token = localStorage.getItem('nexus_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
        };
    },

    async handleResponse(response) {
        const text = await response.text();

        // Handle empty responses (like 404 with no body)
        if (!text || text.trim() === '') {
            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Resource not found');
                }
                if (response.status === 401 || response.status === 403) {
                    throw new Error('Authentication failed. You may not have permission to perform this action.');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return null;
        }

        let data;
        try {
            data = JSON.parse(text);
        } catch (e) {
            console.error('Failed to parse response:', text);
            throw new Error('Invalid response format');
        }

        if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
                throw new Error(data.message || 'Authentication failed. You may not have permission to perform this action.');
            }
            throw new Error(data.message || `HTTP error! status: ${response.status}`);
        }

        return data;
    },

    /**
     * Core request method with 401 interception and token refresh retry.
     */
    async request(method, endpoint, data, params, isRetry = false) {
        const queryString = params ? new URLSearchParams(params).toString() : '';
        const url = queryString
            ? `${this.baseURL}${endpoint}?${queryString}`
            : `${this.baseURL}${endpoint}`;

        const options = {
            method,
            headers: this.getHeaders()
        };

        if (data !== undefined && data !== null) {
            options.body = JSON.stringify(data);
        }

        const response = await fetch(url, options);

        // 401 interception
        if (response.status === 401 && !isRetry) {
            // Check for session invalidation header
            if (response.headers.get('X-Session-Invalid') === 'true') {
                if (logoutCallback) {
                    logoutCallback('You have been logged out because your account was signed in from another location.');
                }
                throw new Error('Session invalidated');
            }

            // Attempt token refresh
            const refreshToken = localStorage.getItem('nexus_refresh_token');
            if (refreshToken) {
                try {
                    const newTokens = await this.doRefreshToken(refreshToken);
                    if (newTokens) {
                        // Retry the original request once with the new token
                        return this.request(method, endpoint, data, params, true);
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                }
            }

            // Refresh failed or no refresh token — force logout
            if (logoutCallback) {
                logoutCallback('Your session has expired. Please sign in again.');
            }
            throw new Error('Authentication failed');
        }

        return await this.handleResponse(response);
    },

    /**
     * Refresh the access token. Deduplicates concurrent calls.
     */
    async doRefreshToken(refreshToken) {
        if (refreshPromise) {
            return refreshPromise;
        }

        refreshPromise = (async () => {
            try {
                const url = `${this.baseURL}/auth/refresh`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken })
                });

                if (!response.ok) {
                    return null;
                }

                const result = await response.json();
                const loginData = result.data || result;

                if (loginData.token) {
                    localStorage.setItem('nexus_token', loginData.token);
                }
                if (loginData.refreshToken) {
                    localStorage.setItem('nexus_refresh_token', loginData.refreshToken);
                }
                if (refreshCallback && Array.isArray(loginData.permissions)) {
                    refreshCallback(loginData);
                }

                return loginData;
            } catch (e) {
                console.error('Refresh token request failed:', e);
                return null;
            } finally {
                refreshPromise = null;
            }
        })();

        return refreshPromise;
    },

    async get(endpoint, params = {}) {
        return this.request('GET', endpoint, null, params);
    },

    async post(endpoint, data) {
        return this.request('POST', endpoint, data);
    },

    async put(endpoint, data) {
        return this.request('PUT', endpoint, data);
    },

    async patch(endpoint, data = {}) {
        return this.request('PATCH', endpoint, data);
    },

    async delete(endpoint) {
        return this.request('DELETE', endpoint);
    }
};

export default apiService;
